import base64 from 'react-native-base64';
import { firebase } from '../config/configureFirebase';
import AccessKeyImport from './AccessKey';
import store from '../store/store';

/** Hermes/Metro a veces entrega el default export como `{ default: "..." }`. */
const AccessKey =
  typeof AccessKeyImport === 'string'
    ? AccessKeyImport
    : (AccessKeyImport && typeof AccessKeyImport.default === 'string'
      ? AccessKeyImport.default
      : '');

const encodeBasic = (value) => {
  try {
    if (typeof globalThis !== 'undefined' && typeof globalThis.btoa === 'function') {
      return globalThis.btoa(value);
    }
  } catch (_) { /* fall through */ }
  if (base64 && typeof base64.encode === 'function') {
    return base64.encode(value);
  }
  if (typeof base64 === 'function') {
    return base64(value);
  }
  throw new Error('No base64 encoder available');
};

/** Caché en memoria agresiva: menos llamadas a Google sin tocar Places quality. */
const CACHE_LIMIT = 400;
const cacheStore = new Map();

const CACHE_TTL = {
  autocomplete: 20 * 60 * 1000,
  place: 24 * 60 * 60 * 1000,
  geocode: 90 * 60 * 1000,
  direction: 12 * 60 * 1000,
  matrix: 8 * 60 * 1000,
};

const getProjectId = () => {
  const { config } = firebase;
  return config?.projectId || 'conecta2-300c3';
};

const getHostCandidates = () => {
  const { config } = firebase;
  const settings = store.getState()?.settingsdata?.settings;
  const projectId = getProjectId();
  const isWebOrigin =
    typeof window !== 'undefined' &&
    window.location &&
    settings?.CompanyWebsite &&
    settings.CompanyWebsite === window.location.origin;
  if (isWebOrigin) {
    return [window.location.origin];
  }
  // Hosting primero; Cloud Functions como respaldo si el rewrite falla.
  return [
    `https://${projectId}.web.app`,
    `https://us-central1-${projectId}.cloudfunctions.net`,
  ];
};

const authHeader = () => {
  const projectId = getProjectId();
  const key = AccessKey || '';
  return {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    Authorization: 'Basic ' + encodeBasic(projectId + ':' + key),
  };
};

const cacheGet = (key) => {
  const hit = cacheStore.get(key);
  if (!hit) return null;
  if (Date.now() > hit.expires) {
    cacheStore.delete(key);
    return null;
  }
  // LRU: reinsert
  cacheStore.delete(key);
  cacheStore.set(key, hit);
  return hit.value;
};

const cacheSet = (key, value, ttlMs) => {
  if (cacheStore.size >= CACHE_LIMIT) {
    const oldest = cacheStore.keys().next().value;
    cacheStore.delete(oldest);
  }
  cacheStore.set(key, { value, expires: Date.now() + ttlMs });
};

const roundCoord = (n, digits = 4) => {
  const x = parseFloat(n);
  if (Number.isNaN(x)) return n;
  return x.toFixed(digits);
};

const normalizeLatLngKey = (latlng) => {
  if (!latlng || typeof latlng !== 'string') return String(latlng || '');
  const parts = latlng.split(',').map((s) => s.trim());
  if (parts.length < 2) return latlng;
  return `${roundCoord(parts[0])},${roundCoord(parts[1])}`;
};

const postGoogleApiToHost = async (host, body) => {
  const response = await fetch(`${host}/googleapi`, {
    method: 'POST',
    headers: authHeader(),
    body: JSON.stringify(body),
  });
  const text = await response.text();
  let json;
  try {
    json = text ? JSON.parse(text) : {};
  } catch (e) {
    throw new Error(`googleapi non-JSON (${response.status})`);
  }
  if (json && json.error && !json.searchResults && !json.coords && !json.address) {
    const msg = typeof json.error === 'string' ? json.error : 'googleapi error';
    const err = new Error(msg);
    err.code = 'GOOGLEAPI_ERROR';
    err.payload = json;
    throw err;
  }
  if (!response.ok && (!json || (!json.searchResults && !json.coords && !json.address))) {
    throw new Error(`googleapi HTTP ${response.status}`);
  }
  return json;
};

const postGoogleApi = async (body) => {
  const hosts = getHostCandidates();
  let lastErr;
  for (let i = 0; i < hosts.length; i++) {
    try {
      return await postGoogleApiToHost(hosts[i], body);
    } catch (err) {
      lastErr = err;
    }
  }
  throw lastErr || new Error('googleapi unreachable');
};

/** Fallback gratuito (OSM) si el backend Google falla — mantiene búsqueda usable. */
const nominatimAutocomplete = async (searchKeyword) => {
  const q = (searchKeyword || '').trim();
  if (!q) return [];
  const url =
    'https://nominatim.openstreetmap.org/search?format=json&addressdetails=0&limit=6&countrycodes=co&q=' +
    encodeURIComponent(q);
  const response = await fetch(url, {
    method: 'GET',
    headers: {
      Accept: 'application/json',
      'Accept-Language': 'es',
      'User-Agent': 'Conecta2App/2.2.5 (conecta2; mobile)',
    },
  });
  if (!response.ok) {
    throw new Error(`nominatim HTTP ${response.status}`);
  }
  const json = await response.json();
  if (!Array.isArray(json)) return [];
  return json
    .filter((item) => item && item.display_name && item.lat && item.lon)
    .map((item) => ({
      description: item.display_name,
      lat: parseFloat(item.lat),
      lng: parseFloat(item.lon),
      // sin place_id de Google → SearchScreen usa lat/lng directo
    }));
};

export const fetchPlacesAutocomplete = (searchKeyword, sessionToken) => {
  return new Promise((resolve, reject) => {
    const q = (searchKeyword || '').trim();
    if (!q) {
      resolve([]);
      return;
    }
    const cacheKey = `ac:${q.toLowerCase()}`;
    const cached = cacheGet(cacheKey);
    if (cached) {
      resolve(cached);
      return;
    }

    postGoogleApi({
      searchKeyword: q,
      sessiontoken: sessionToken || undefined,
    })
      .then((json) => {
        if (json && Array.isArray(json.searchResults)) {
          cacheSet(cacheKey, json.searchResults, CACHE_TTL.autocomplete);
          resolve(json.searchResults);
          return;
        }
        if (json && json.searchResults) {
          cacheSet(cacheKey, json.searchResults, CACHE_TTL.autocomplete);
          resolve(json.searchResults);
          return;
        }
        // Backend respondió sin resultados → intentar OSM antes de fallar
        return nominatimAutocomplete(q).then((osm) => {
          cacheSet(cacheKey, osm, CACHE_TTL.autocomplete);
          resolve(osm);
        });
      })
      .catch((err) => {
        if (typeof __DEV__ !== 'undefined' && __DEV__) {
          console.warn('[fetchPlacesAutocomplete] Backend failed, trying OSM', err);
        }
        nominatimAutocomplete(q)
          .then((osm) => {
            cacheSet(cacheKey, osm, Math.min(CACHE_TTL.autocomplete, 5 * 60 * 1000));
            resolve(osm);
          })
          .catch((osmErr) => {
            reject(err?.message || osmErr?.message || 'fetchPlacesAutocomplete Call Error');
          });
      });
  });
};

export const fetchCoordsfromPlace = (place_id, sessionToken) => {
  return new Promise((resolve, reject) => {
    if (!place_id) {
      reject('No place_id');
      return;
    }
    const cacheKey = `place:${place_id}`;
    const cached = cacheGet(cacheKey);
    if (cached) {
      resolve(cached);
      return;
    }

    postGoogleApi({
      place_id,
      sessiontoken: sessionToken || undefined,
    })
      .then((json) => {
        if (json && json.coords) {
          cacheSet(cacheKey, json.coords, CACHE_TTL.place);
          resolve(json.coords);
        } else {
          if (typeof __DEV__ !== 'undefined' && __DEV__ && json && json.error) {
            console.warn('[fetchCoordsfromPlace] Backend returned error:', json.error);
          }
          reject(json?.error || 'No coords');
        }
      })
      .catch(() => {
        if (typeof __DEV__ !== 'undefined' && __DEV__) {
          console.warn('[fetchCoordsfromPlace] Network/Call Error');
        }
        reject('fetchCoordsfromPlace Call Error');
      });
  });
};

export const fetchAddressfromCoords = (latlng) => {
  return new Promise((resolve, reject) => {
    const keyLatLng = normalizeLatLngKey(latlng);
    const cacheKey = `geo:${keyLatLng}`;
    const cached = cacheGet(cacheKey);
    if (cached) {
      resolve(cached);
      return;
    }

    postGoogleApi({ latlng: keyLatLng })
      .then((json) => {
        if (json && json.address) {
          cacheSet(cacheKey, json.address, CACHE_TTL.geocode);
          resolve(json.address);
        } else {
          if (typeof __DEV__ !== 'undefined' && __DEV__ && json && json.error) {
            console.warn('[fetchAddressfromCoords] Backend returned error:', json.error);
          }
          reject(json?.error || 'No address');
        }
      })
      .catch(() => {
        if (typeof __DEV__ !== 'undefined' && __DEV__) {
          console.warn('[fetchAddressfromCoords] Network/Call Error');
        }
        reject('fetchAddressfromCoords Call Error');
      });
  });
};

export const getDistanceMatrix = (startLoc, destLoc) => {
  return new Promise((resolve, reject) => {
    const cacheKey = `mx:${normalizeLatLngKey(startLoc)}|${destLoc}`;
    const cached = cacheGet(cacheKey);
    if (cached) {
      resolve(cached);
      return;
    }

    postGoogleApi({
      start: startLoc,
      dest: destLoc,
      calltype: 'matrix',
    })
      .then((json) => {
        if (json.error) {
          if (typeof __DEV__ !== 'undefined' && __DEV__) {
            console.warn('[getDistanceMatrix] Backend returned error:', json.error);
          }
          reject(json.error);
        } else {
          cacheSet(cacheKey, json, CACHE_TTL.matrix);
          resolve(json);
        }
      })
      .catch(() => {
        if (typeof __DEV__ !== 'undefined' && __DEV__) {
          console.error('[getDistanceMatrix] Network/Call Error');
        }
        reject('getDistanceMatrix Call Error');
      });
  });
};

/** options.traffic: tiempo con el tráfico actual (ruta en vivo); las cotizaciones no lo usan. */
export const getDirectionsApi = (startLoc, destLoc, waypoints, options = {}) => {
  return new Promise((resolve, reject) => {
    const traffic = options && options.traffic === true;
    const cacheKey = `dir:${normalizeLatLngKey(startLoc)}|${normalizeLatLngKey(destLoc)}|${waypoints || ''}${traffic ? '|t' : ''}`;
    const cached = cacheGet(cacheKey);
    if (cached) {
      resolve(cached);
      return;
    }

    const body = {
      start: startLoc,
      dest: destLoc,
      calltype: 'direction',
    };
    if (waypoints) {
      body.waypoints = waypoints;
    }
    if (traffic) {
      body.traffic = true;
    }

    postGoogleApi(body)
      .then((json) => {
        if (json && Object.prototype.hasOwnProperty.call(json, 'distance_in_km')) {
          cacheSet(cacheKey, json, CACHE_TTL.direction);
          resolve(json);
        } else {
          if (
            typeof __DEV__ !== 'undefined' &&
            __DEV__ &&
            json &&
            json.error &&
            json.error !== 'No results found'
          ) {
            console.warn('[getDirectionsApi] Backend returned error:', json.error);
          }
          reject(json?.error || 'No results found');
        }
      })
      .catch(() => {
        if (typeof __DEV__ !== 'undefined' && __DEV__) {
          console.warn('[getDirectionsApi] Network/Call Error');
        }
        reject('getDirectionsApi Call Error');
      });
  });
};
